import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { Button } from '../button';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { typographyVariant } from '../typography';

import { sheetVariants } from './sheet.styles';
import type { SheetContentProps, SheetOverlayProps } from './sheet.types';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

const SheetTrigger = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Trigger>
>(({ ...props }, ref) => (
  <SheetPrimitive.Trigger data-slot="sheet-trigger" ref={ref} {...props} />
));
SheetTrigger.displayName = SheetPrimitive.Trigger.displayName;

const SheetClose = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Close>
>(({ ...props }, ref) => (
  <SheetPrimitive.Close data-slot="sheet-close" ref={ref} {...props} />
));
SheetClose.displayName = SheetPrimitive.Close.displayName;

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  SheetOverlayProps
>(({ className, hideBlur = false, ...props }, ref) => (
  <SheetPrimitive.Overlay
    data-slot="sheet-overlay"
    className={cn(
      'fixed inset-0 z-50 duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none',
      !hideBlur
        ? 'bg-surface/40 supports-[backdrop-filter]:backdrop-blur-sm'
        : 'bg-transparent',
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = 'right',
      className,
      children,
      hideCloseButton,
      hideBlur,
      overlayClassName,
      ...props
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const closeLabel = intl.formatMessage({
      defaultMessage: 'Close',
      id: 'J8AOU7v/Sf',
      description: 'Default accessible label for the Sheet built-in close button',
    });

    return (
      <SheetPortal>
        <SheetOverlay hideBlur={hideBlur} className={overlayClassName} />
        <SheetPrimitive.Content
          data-slot="sheet-content"
          ref={ref}
          className={cn(sheetVariants({ side }), className)}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close data-slot="sheet-close" asChild>
              <Button
                variant="ghost"
                className="absolute end-4 top-4 after:absolute after:-inset-2 after:content-['']"
                flush="all"
                size="xxs"
                iconOnly
                disableAutomaticSvgSizing
              >
                <XIcon className="size-4 shrink-0" aria-hidden="true" />
                <span className="sr-only">{closeLabel}</span>
              </Button>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  }
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-header"
    className={cn('flex flex-col gap-0.5', className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-footer"
    className={cn('mt-auto flex flex-col gap-2', className)}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    data-slot="sheet-title"
    ref={ref}
    className={cn(typographyVariant({ variant: 'h4' }), className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    data-slot="sheet-description"
    ref={ref}
    className={cn(typographyVariant({ variant: 'p2' }), 'text-text-secondary', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
